import { Activity, EllipsisVertical } from 'lucide-react';
import { overlay } from 'overlay-kit';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/shadcn/ui/dropdown-menu';
import { UpdateSignalDialog } from '@/features/(signal)/signal-create-dialog';
import { DeleteSignalDialog } from '@/features/(signal)/signal-delete-dialog';
import useWorkspaceId from '@/hooks/domain/useWorkspaceId';
import { useOptimisticSearchParam } from '@/hooks/pure/useOptimisticSearchParam';
import { cn } from '@/lib/cn';

type Props = {
  id: string;
  title: string;
  definition?: string;
  count: number;
  hiddenMoreAction?: boolean;
};

export default function SignalButton({
  id,
  title,
  definition,
  count,
  hiddenMoreAction,
}: Props) {
  const [activeSignalId, setActiveSignalId] =
    useOptimisticSearchParam('signal');
  const isActive = activeSignalId === id;

  const handleClick = () => {
    setActiveSignalId(id);
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 px-4 py-2 rounded-md bg-secondary cursor-pointer transition-colors hover:bg-dune-200 border border-transparent',
        isActive && 'border-primary',
      )}
      onClick={handleClick}
      role="button"
    >
      <div className="flex items-center justify-between gap-2">
        <Activity className="size-3.5" />
        <div className="text-sm font-semibold text-gray-900 max-w-[120px] truncate">
          {title}
        </div>
        <div className="text-xs text-dune-500">{count}</div>
      </div>
      {!hiddenMoreAction && (
        <SignalActionDropdown id={id} title={title} definition={definition} />
      )}
    </div>
  );
}

const SignalActionDropdown = ({ id = '', title = '', definition = '' }) => {
  const workspaceId = useWorkspaceId();

  const handleEdit = () => {
    const defaultValues = {
      title,
      definition,
    };
    overlay.open(({ isOpen, close, unmount }) => (
      <UpdateSignalDialog
        isOpen={isOpen}
        workspaceId={workspaceId}
        close={close}
        unmount={unmount}
        defaultValues={defaultValues}
        signalId={id}
      />
    ));
  };

  const handleDelete = () => {
    overlay.open(({ isOpen, close, unmount }) => (
      <DeleteSignalDialog
        signalId={id}
        // TODO: signal alert api 연동시 확인 필요
        alertNames={[]}
        isOpen={isOpen}
        close={close}
        unmount={unmount}
      />
    ));
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors outline-none disabled:pointer-events-none disabled:opacity-50 hover:text-accent-foreground text-muted-foreground"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <EllipsisVertical className="h-4 w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleEdit();
          }}
        >
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            handleDelete();
          }}
        >
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
